import { useCallback, useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { ShopList } from '~/components/ui/ShopList';
import { ShopListItem } from '~/components/ui/ShopListItem';
import { rage, useInputAction } from '~/rage';
import { InputAction, WalletType } from '~/shared';
import { useBuy } from '../../hooks';
import {
	setActiveList,
	setActiveListIndex,
	setActiveStage,
	setMaxListIndex,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CategoryImages, CategoryNames } from '../CategoryTab';

const ListTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const windowTints = rage.vehiclePaintShop.getVehicleWindowTints().getValue();
	const activeListIndex = useLayoutSelector(
		(state) => state.carPaint.activeListIndex,
	);
	const maxListIndex = useLayoutSelector(
		(state) => state.carPaint.maxListIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.carPaint.activeCategory,
	);
	const ownedVehiclePaint = useLayoutSelector(
		(state) => state.carPaint.ownedVehiclePaint,
	);
	const { handleBuy } = useBuy();

	const items = useMemo(
		() =>
			windowTints.map((item) => ({
				name: item.name,
				price: item.price,
				value: item.value,
			})),
		[windowTints, ownedVehiclePaint?.windowTint],
	);

	const handleCheckInstall = useCallback(
		(index: number) => {
			return ownedVehiclePaint?.windowTint === items[index].value;
		},
		[items, ownedVehiclePaint?.windowTint],
	);

	const handleSelectItem = useCallback(
		(index: number | null) => {
			if (index !== null) {
				if (handleCheckInstall(index)) {
					return;
				}
			}

			dispatch(setActiveListIndex(index));
		},
		[handleCheckInstall],
	);

	const handleBack = useCallback(() => {
		dispatch(setActiveStage('category'));
	}, []);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (activeListIndex === null) {
			return;
		}

		handleBuy(WalletType.Cash);
	});

	useEffect(() => {
		if (activeListIndex === null) {
			return;
		}

		const item = items[activeListIndex];
		rage.vehiclePaintShop
			.previewWindowTint(item.value)
			.catch((err) => rage.notifications.showException(err));
	}, [activeListIndex, items]);

	useEffectOnce(() => {
		dispatch(setMaxListIndex(items.length - 1));
		dispatch(setActiveList(items));
	});

	if (!activeCategory) {
		return;
	}

	return (
		<ShopList
			icon={CategoryImages[activeCategory]}
			title={CategoryNames[activeCategory]}
			style={{ marginTop: 30 }}
			handleBack={handleBack}
			activeIndex={activeListIndex}
			maxIndex={maxListIndex}
			handleCheckInstall={handleCheckInstall}
			handleSelect={handleSelectItem}
		>
			{items.map((item, index) => (
				<ShopListItem
					title={item.name}
					price={item.price}
					walletType={WalletType.Cash}
					id={`paint-${index}`}
					handleClick={() => handleSelectItem(index)}
					active={index === activeListIndex}
					installed={handleCheckInstall(index)}
					key={index}
				/>
			))}
		</ShopList>
	);
};

export { ListTab };
